<script setup lang="ts">
import { ref, watch } from 'vue'
import { MapPin } from 'lucide-vue-next'

interface BillingAddress {
  address_id?: number | null
  firstName: string
  lastName: string
  email: string
  streetAddress: string
  city: string
  postcode: string
  country: string
}

interface SavedAddress {
  id: number
  first_name: string
  last_name: string
  email: string | null
  address1: string
  address2: string | null
  city: string
  postcode: string
  country: string
  is_billing_default: boolean
}

interface Props {
  modelValue: BillingAddress
  savedAddresses?: SavedAddress[]
  userEmail?: string
}

const props = withDefaults(defineProps<Props>(), {
  savedAddresses: () => [],
  userEmail: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: BillingAddress]
}>()

const useExistingAddress = ref(false)
const selectedAddressId = ref<number | null>(null)

const countries = [
  'United Kingdom',
  'United States',
  'Canada',
  'Australia',
  'Ireland',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
]

// When checkbox is toggled
watch(useExistingAddress, (checked) => {
  if (checked && props.savedAddresses.length > 0) {
    // Select default address or first one
    const defaultAddress = props.savedAddresses.find(a => a.is_billing_default) || props.savedAddresses[0]
    if (defaultAddress) {
      selectedAddressId.value = defaultAddress.id
      selectAddress(defaultAddress)
    }
  } else {
    // Clear selection and reset form
    selectedAddressId.value = null
    emit('update:modelValue', {
      address_id: null,
      firstName: '',
      lastName: '',
      email: props.userEmail || '',
      streetAddress: '',
      city: '',
      postcode: '',
      country: 'United Kingdom',
    })
  }
})

function selectAddress(address: SavedAddress) {
  emit('update:modelValue', {
    address_id: address.id,
    firstName: address.first_name || '',
    lastName: address.last_name || '',
    email: address.email || props.userEmail || '',
    streetAddress: address.address1 || '',
    city: address.city || '',
    postcode: address.postcode || '',
    country: address.country || 'United Kingdom',
  })
}

function handleAddressSelect(addressId: number) {
  selectedAddressId.value = addressId
  const address = props.savedAddresses.find(a => a.id === addressId)
  if (address) {
    selectAddress(address)
  }
}

function updateField(field: keyof BillingAddress, event: Event) {
  const target = event.target as HTMLInputElement | HTMLSelectElement
  emit('update:modelValue', { ...props.modelValue, [field]: target.value })
}

function formatAddressPreview(address: SavedAddress): string {
  const parts = [address.address1, address.city, address.postcode].filter(Boolean)
  return parts.join(', ')
}
</script>

<template>
  <section class="bg-white rounded-2xl shadow-sm p-4 md:p-8">
    <h2 class="text-xl font-semibold text-gray-900 mb-6">Billing Address</h2>

    <!-- Use Existing Address Checkbox -->
    <div v-if="savedAddresses.length > 0" class="mb-6">
      <label class="flex items-center gap-3 cursor-pointer">
        <input
          v-model="useExistingAddress"
          type="checkbox"
          class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <span class="text-sm font-medium text-gray-700">Use an existing address</span>
      </label>
    </div>

    <!-- Saved Addresses List (shown when checkbox is checked) -->
    <div v-if="useExistingAddress && savedAddresses.length > 0" class="space-y-3">
      <label
        v-for="address in savedAddresses"
        :key="address.id"
        :class="[
          'flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all',
          selectedAddressId === address.id
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300',
        ]"
      >
        <input
          type="radio"
          :value="address.id"
          :checked="selectedAddressId === address.id"
          @change="handleAddressSelect(address.id)"
          class="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
        />
        <div class="ml-3 flex-1">
          <div class="flex items-center gap-2">
            <MapPin class="w-4 h-4 text-gray-400" />
            <span class="font-medium text-gray-900">
              {{ address.first_name }} {{ address.last_name }}
            </span>
            <span
              v-if="address.is_billing_default"
              class="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full"
            >
              Default
            </span>
          </div>
          <p class="text-sm text-gray-500 mt-1">{{ formatAddressPreview(address) }}</p>
        </div>
      </label>
    </div>

    <!-- Address Form (shown when checkbox is unchecked or no saved addresses) -->
    <form
      v-show="!useExistingAddress || savedAddresses.length === 0"
      class="space-y-6"
      @submit.prevent
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
          <input
            type="text"
            :value="modelValue.firstName"
            @input="updateField('firstName', $event)"
            placeholder="John"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
          <input
            type="text"
            :value="modelValue.lastName"
            @input="updateField('lastName', $event)"
            placeholder="Smith"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
        <input
          type="email"
          :value="modelValue.email"
          @input="updateField('email', $event)"
          placeholder="john.smith@example.com"
          class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-2">Street Address</label>
        <input
          type="text"
          :value="modelValue.streetAddress"
          @input="updateField('streetAddress', $event)"
          placeholder="123 Main Street"
          class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">City</label>
          <input
            type="text"
            :value="modelValue.city"
            @input="updateField('city', $event)"
            placeholder="London"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">Postcode</label>
          <input
            type="text"
            :value="modelValue.postcode"
            @input="updateField('postcode', $event)"
            placeholder="SW1A 1AA"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-2">Country</label>
        <select
          :value="modelValue.country"
          @change="updateField('country', $event)"
          class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option v-for="country in countries" :key="country" :value="country">
            {{ country }}
          </option>
        </select>
      </div>
    </form>
  </section>
</template>
